import { useEffect, useRef, useState, type ReactNode } from "react"
import { RefreshCw } from "lucide-react"
import styles from "./PullToRefresh.module.css"

const THRESHOLD = 70
const MAX_PULL = 110

interface PullToRefreshProps {
  children: ReactNode
}

export function PullToRefresh({ children }: PullToRefreshProps) {
  const [pullDistance, setPullDistance] = useState(0)
  const [isRefreshing, setIsRefreshing] = useState(false)
  const startY = useRef(0)
  const isPulling = useRef(false)
  const pullRef = useRef(0)

  useEffect(() => {
    const onTouchStart = (e: TouchEvent) => {
      const touch = e.touches[0]
      if (touch && window.scrollY === 0 && !isRefreshing) {
        startY.current = touch.clientY
        isPulling.current = true
      }
    }

    const onTouchMove = (e: TouchEvent) => {
      if (!isPulling.current || isRefreshing) return
      const touch = e.touches[0]
      if (!touch) return
      const delta = touch.clientY - startY.current
      if (delta > 0 && window.scrollY === 0) {
        e.preventDefault()
        const next = Math.min(delta * 0.5, MAX_PULL)
        pullRef.current = next
        setPullDistance(next)
      } else {
        isPulling.current = false
        pullRef.current = 0
        setPullDistance(0)
      }
    }

    const onTouchEnd = () => {
      if (!isPulling.current) return
      isPulling.current = false
      if (pullRef.current >= THRESHOLD) {
        setIsRefreshing(true)
        setPullDistance(THRESHOLD)
        window.setTimeout(() => window.location.reload(), 400)
      } else {
        pullRef.current = 0
        setPullDistance(0)
      }
    }

    document.addEventListener("touchstart", onTouchStart, { passive: true })
    document.addEventListener("touchmove", onTouchMove, { passive: false })
    document.addEventListener("touchend", onTouchEnd)

    return () => {
      document.removeEventListener("touchstart", onTouchStart)
      document.removeEventListener("touchmove", onTouchMove)
      document.removeEventListener("touchend", onTouchEnd)
    }
  }, [isRefreshing])

  const progress = Math.min(pullDistance / THRESHOLD, 1)

  return (
    <>
      <div
        className={styles.indicator}
        style={{ height: `${pullDistance}px`, opacity: progress }}
        aria-hidden="true"
      >
        <RefreshCw
          size={20}
          className={isRefreshing ? styles.spinning : undefined}
          style={!isRefreshing ? { transform: `rotate(${progress * 360}deg)` } : undefined}
        />
      </div>
      <div
        className={styles.content}
        style={
          pullDistance > 0
            ? {
                transform: `translateY(${pullDistance}px)`,
                transition: isPulling.current ? "none" : "transform 0.25s ease-out",
              }
            : undefined
        }
      >
        {children}
      </div>
    </>
  )
}
